import { Component, OnInit, OnDestroy, inject, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { catchError, finalize, forkJoin, map, of, switchMap } from 'rxjs';
import { AdminDashboardPageService } from '../admin-dashboard-page.service';
import { ImageService } from '../../../../shared/services/image.service';
import { LoadMaskComponent } from '../../../../shared/load-mask/load-mask.component';
import { PATH } from '../../../../../assets/constants';
import { IAdminUser, IApplication, IApplicationWithOffer } from '../../../../../assets/types/dtoTypes';
import { ApplicationsOverviewPageService } from '../../user/applications-overview-page/applications-overview-page.service';

type SortKey = 'recent' | 'name' | 'applications';
type UserRow = IAdminUser;

@Component({
  selector: 'app-user-page',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule, LoadMaskComponent],
  templateUrl: './user-page.component.html',
  styleUrls: ['./user-page.component.scss'],
})
export class UserPageComponent implements OnInit, OnDestroy {
  private readonly adminService = inject(AdminDashboardPageService);
  private readonly imageService = inject(ImageService);
  private readonly applicationOverviewService = inject(ApplicationsOverviewPageService);
  private readonly router = inject(Router);
  protected readonly Math = Math;
  isLoading = true;

  users = signal<UserRow[]>([]);
  totalUsers = signal<number>(0);

  searchTerm = signal<string>('');
  withApplicationOnly = signal<boolean>(false);

  selectedCountry = signal<string>('');
  selectedLangs = signal<Set<string>>(new Set());

  selectedLookingFor = signal<string>('');
  selectedDomain = signal<string>('');

  sortBy = signal<SortKey>('recent');

  page = signal<number>(1);
  pageSize = signal<number>(10);

  openMenuIndex = signal<number | null>(null);

  expandedUsers = signal<Set<string>>(new Set());
  userApplicationDetails = signal<Map<string, IApplicationWithOffer[]>>(new Map());
  loadingApplicationsFor = signal<Set<string>>(new Set());

  countries = signal<string[]>([]);
  languageLevels = signal<string[]>([]);
  lookingForOptions = signal<string[]>([]);
  domainOptions = signal<string[]>([]);

  private readonly onKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Escape') this.openMenuIndex.set(null);
  };

  ngOnInit(): void {
    this.loadUsers();
    window.addEventListener('keydown', this.onKeyDown);
  }

  ngOnDestroy(): void {
    window.removeEventListener('keydown', this.onKeyDown);
  }

  loadUsers(): void {
    this.isLoading = true;

    this.adminService
      .getAdminUsers()
      .pipe(
        switchMap((users: IAdminUser[]) => {
          this.totalUsers.set(users.length);

          const withSas$ = users.map((u) =>
            u.profileImageUrl
              ? this.imageService
                .appendSasTokenToProfileImageUrl(u.profileImageUrl, u.id)
                .pipe(
                  map((updatedUrl) => ({ ...u, profileImageUrl: updatedUrl } as UserRow)),
                  catchError((err) => {
                    console.error('SAS token append failed for user', u.id, err);
                    return of(u as UserRow);
                  })
                )
              : of(u as UserRow)
          );

          return withSas$.length ? forkJoin(withSas$) : of(users as UserRow[]);
        }),
        finalize(() => (this.isLoading = false))
      )
      .subscribe({
        next: (rows: UserRow[]) => {
          this.users.set(rows);

          this.countries.set(
            this.distinct(rows.map((x) => (x.country || '').toString()).filter(Boolean))
          );
          this.languageLevels.set(
            this.distinct(rows.map((x) => (x.languageLevel || '').toString()).filter(Boolean))
          );
          this.lookingForOptions.set(
            this.distinct(rows.map((x) => (x.lookingFor || '').toString()).filter(Boolean))
          );
          this.domainOptions.set(
            this.distinct(rows.map((x) => (x.domain || '').toString()).filter(Boolean))
          );
        },
        error: (err) => console.error('Error loading users', err),
      });
  }

  filteredUsers = computed(() => {
    const term = this.searchTerm().trim().toLowerCase();
    const withApps = this.withApplicationOnly();

    const country = this.selectedCountry();
    const langs = this.selectedLangs();

    const lookingFor = this.selectedLookingFor();
    const domain = this.selectedDomain();

    let list = [...this.users()];

    if (term) {
      list = list.filter((u) => {
        const name = ((u.firstname || '') + ' ' + (u.lastname || '')).trim();
        const email = (u.email || '').toString();
        return [name, email].some((v) => v.toLowerCase().includes(term));
      });
    }

    if (withApps) {
      list = list.filter((u) => (u.applicationsCount ?? 0) > 0);
    }

    if (country) {
      list = list.filter((u) => u.country === country);
    }

    if (langs.size) {
      list = list.filter((u) => langs.has((u.languageLevel || '').toString()));
    }

    if (lookingFor) {
      list = list.filter((u) => (u.lookingFor || '') === lookingFor);
    }

    if (domain) {
      list = list.filter((u) => (u.domain || '') === domain);
    }

    const sort = this.sortBy();
    list.sort((a, b) => {
      switch (sort) {
        case 'name': {
          const an = ((a.firstname || '') + ' ' + (a.lastname || '')).trim().toLowerCase();
          const bn = ((b.firstname || '') + ' ' + (b.lastname || '')).trim().toLowerCase();
          return an.localeCompare(bn);
        }
        case 'applications': {
          const ac = a.applicationsCount ?? 0;
          const bc = b.applicationsCount ?? 0;
          return bc - ac;
        }
        default: {
          const ad = new Date(a.registrationDate || 0).getTime();
          const bd = new Date(b.registrationDate || 0).getTime();
          return bd - ad;
        }
      }
    });

    return list;
  });

  paginatedUsers = computed(() => {
    const p = this.page();
    const size = this.pageSize();
    const start = (p - 1) * size;
    const end = start + size;
    return this.filteredUsers().slice(start, end);
  });

  trackByUser = (_: number, u: UserRow) => u.id;

  toggleLang(label: string): void {
    const s = new Set(this.selectedLangs());
    s.has(label) ? s.delete(label) : s.add(label);
    this.selectedLangs.set(s);
    this.page.set(1);
  }

  clearFilters(): void {
    this.withApplicationOnly.set(false);
    this.selectedCountry.set('');
    this.selectedLangs.set(new Set());
    this.selectedLookingFor.set('');
    this.selectedDomain.set('');
    this.searchTerm.set('');
    this.sortBy.set('recent');
    this.page.set(1);
  }

  prevPage(): void {
    if (this.page() > 1) this.page.update((v) => v - 1);
  }

  nextPage(): void {
    const total = this.filteredUsers().length;
    const maxPage = Math.max(1, Math.ceil(total / this.pageSize()));
    if (this.page() < maxPage) this.page.update((v) => v + 1);
  }

  viewProfile(id: string): void {
    this.router.navigate([PATH.PROFIL, id]);
  }

  displayName(u: UserRow): string {
    const full = `${u.firstname || ''} ${u.lastname || ''}`.trim();
    return full || 'Unknown';
  }

  /** Humanizes enum-like values (e.g. "AUSBILDUNG_TRAINING" -> "Ausbildung Training"). */
  prettyLabel(v?: string): string {
    if (!v) return '—';
    return v
      .replace(/_/g, ' ')
      .toLowerCase()
      .replace(/\b\w/g, (c) => c.toUpperCase());
  }

  initials(u: UserRow): string {
    const f = (u.firstname || '').trim();
    const l = (u.lastname || '').trim();
    const i = `${f.charAt(0)}${l.charAt(0)}`.toUpperCase();
    return i || (u.email || '?').charAt(0).toUpperCase();
  }

  registeredAt(u: UserRow): string {
    const date = new Date(u.registrationDate);
    const options: Intl.DateTimeFormatOptions = { day: '2-digit', month: 'short', year: 'numeric' };
    const time = date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    return `${date.toLocaleDateString(undefined, options)} · ${time}`;
  }

  private distinct(arr: string[]): string[] {
    const set = new Set(arr.map((a) => a.trim()));
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }

  isExpanded(userId: string): boolean {
    return this.expandedUsers().has(userId);
  }

  isLoadingApps(userId: string): boolean {
    return this.loadingApplicationsFor().has(userId);
  }

  toggleApplications(userId: string): void {
    const expanded = new Set(this.expandedUsers());
    if (expanded.has(userId)) {
      expanded.delete(userId);
      this.expandedUsers.set(expanded);
      return;
    }
    expanded.add(userId);
    this.expandedUsers.set(expanded);

    if (this.userApplicationDetails().has(userId)) return;

    this.loadingApplicationsFor.update(s => new Set([...s, userId]));

    this.applicationOverviewService.getAllApplicationsByUserId(userId).pipe(
      switchMap((applications: IApplication[]) => {
        if (!applications.length) return of([] as IApplicationWithOffer[]);
        const requests = applications.map(app =>
          this.applicationOverviewService.getJobOfferById(app.jobOfferId).pipe(
            map(jobOffer => ({ ...app, jobOffer }) as IApplicationWithOffer),
            catchError(() => of({ ...app, jobOffer: null as any }) as any)
          )
        );
        return forkJoin(requests) as any;
      }),
      finalize(() => {
        this.loadingApplicationsFor.update(s => {
          const next = new Set(s);
          next.delete(userId);
          return next;
        });
      })
    ).subscribe({
      next: (apps: IApplicationWithOffer[]) => {
        this.userApplicationDetails.update(m => {
          const next = new Map(m);
          next.set(userId, apps);
          return next;
        });
      },
      error: (err) => console.error('Failed to load applications for user', userId, err),
    });
  }

  formatDate(dateStr: string): string {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
  }

  removeUser(user: UserRow): void {
    const confirmed = window.confirm(`Are you sure you want to remove ${this.displayName(user)}?`);

    if (!confirmed) {
      return;
    }

    this.isLoading = true;

    this.applicationOverviewService.deleteUser(user.id)
      .pipe(
        finalize(() => {
          this.isLoading = false;
          this.openMenuIndex.set(null);
        })
      )
      .subscribe({
        next: () => {
          this.users.update((users) => users.filter((u) => u.id !== user.id));
          this.totalUsers.update((count) => Math.max(0, count - 1));
        },
        error: (err) => {
          console.error('Failed to delete user', err);
          alert('Failed to remove user.');
        },
      });
  }
}
